import { getLinkModuleFile, getModuleFileTwo } from '~src/services/api/modules';
import { DownloadMultipleModuleFilesParams } from '~src/utils/downloader/types/types';
import { DownloadingState, useStore } from '~src/store';
import * as zip from '@zip.js/zip.js';
import streamSaver from 'streamsaver';
import { Modal } from 'antd';

// --- ZipStream Class ---
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  private fileMap: Map<string, number> = new Map();
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  public totalSize = 0;
  private downloadedSize = 0;
  private startTime = 0;
  private endTime = 0;
  public isCancelled = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isCancelled = true;
    }
  }

  cancel() {
    this.isCancelled = true;
    this.writableStream = null;
    this.zipWriter = null;
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';
    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;
    const uniqueName = this.getUniqueFileName(name);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      this.downloadedSize += blob.size;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | Modules`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | Modules`
        );
      this.isCancelled = true;
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;
      this.endTime = performance.now();
      const totalSizeMB = (this.downloadedSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.downloadedSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isCancelled = true;
    }
  }
}

// --- Worker Creation ---
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// --- Modal Confirm Download (unchanged) ---
const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Too much files selected (50+ files). There can be problems with downloading. We recommend downloading to reduce total files selected to 50 max. at once.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      },
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

// splits an array into chunks of given size.
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

// fetch URLs for a given chunk of moduleFiles.
// Each file is wrapped in a try-catch; if getLinkModuleFile fails (e.g. 404),
// we return an object with a fetchError property.
async function fetchUrlsForChunk(chunk: any[]): Promise<any[]> {
  const tasks = await Promise.all(
    chunk.map(async (item) => {
      try {
        const fileUrl = await getModuleFileTwo({
          projectId: String(item.project_id),
          moduleFileId: String(item.id),
          moduleId: item.module,
          response_type: 'base64',
        });
        return {
          ...item,
          url: fileUrl,
          title: item.file_name,
          fetchError: null,
        };
      } catch (error) {
        return {
          ...item,
          url: null,
          title: item.file_name,
          fetchError: error.message,
        };
      }
    })
  );
  return tasks;
}

export const downloadMultipleModuleFiles = async ({
  moduleFiles,
  onDownloadStarted,
}: DownloadMultipleModuleFilesParams): Promise<void> => {
  useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);

  let startDownload = true;
  if (moduleFiles.length > 50) {
    startDownload = await ModalConfirmDownload();
  }
  if (!startDownload) return;

  const totalFiles = moduleFiles.length;

  onDownloadStarted && onDownloadStarted();

  const zipStream = new ZipStream(totalFiles, 0);
  zipStream.start();
  if (zipStream.isCancelled) return;

  // Split files into chunks (if more than 50, use chunks of 10)
  const fileChunks =
    moduleFiles.length > 50 ? chunkArray(moduleFiles, 10) : [moduleFiles];

  // Start fetching URLs for the first chunk.
  let nextChunkPromise = fetchUrlsForChunk(fileChunks[0]);

  // Process each chunk one by one.
  for (let i = 0; i < fileChunks.length; i++) {
    if (zipStream.isCancelled) return;
    // Await the URLs for the current chunk.
    const currentChunk = await nextChunkPromise;

    // If there is another chunk, start fetching its URLs concurrently.
    if (i < fileChunks.length - 1) {
      nextChunkPromise = fetchUrlsForChunk(fileChunks[i + 1]);
    }

    // Separate files that failed to fetch their URL.
    const validFiles = currentChunk.filter((file) => !file.fetchError);
    const errorFiles = currentChunk.filter((file) => file.fetchError);

    // Record fetch errors in the zipStream error log.
    errorFiles.forEach((file) => {
      zipStream.errorLog.push(
        `${file.file_name} (0.00 MB) - ERROR: ${file.fetchError} | Modules`
      );
      useStore
        .getState()
        .addError(
          `${file.file_name} (0.00 MB) - ERROR: ${file.fetchError} | Modules`
        );
    });

    // If there are valid files, process (download and stream) them.
    if (validFiles.length > 0) {
      await processChunk(validFiles, zipStream);
    }
  }

  if (!zipStream.isCancelled) {
    await zipStream.finish();
  }
};

const processChunk = (chunk: any[], zipStream: ZipStream): Promise<void> => {
  return new Promise((resolve) => {
    if (zipStream.isCancelled) return;
    const NUM_WORKERS = 5;
    const fileQueue = [...chunk];
    let pendingTasks = chunk.length;
    let activeWorkers = 0;

    // Create a local pool of workers for this chunk.
    const workers: Worker[] = [];
    for (let i = 0; i < NUM_WORKERS; i++) {
      const workerId = i + 1;
      const worker = createWorker();
      worker.onmessage = async (event) => {
        if (zipStream.isCancelled) {
          workers.forEach((w) => w.terminate());
          return resolve();
        }
        const { success, file, blob, error } = event.data;
        activeWorkers = Math.max(activeWorkers - 1, 0);
        if (success) {
          await zipStream.addFile(file.file_name, blob);
        } else {
          // Log errors from worker fetches (e.g. 404s)
          zipStream.errorLog.push(
            `${file.file_name} (0.00 MB) - ERROR: ${error} | Modules`
          );
          useStore
            .getState()
            .addError(
              `${file.file_name} (0.00 MB) - ERROR: ${error} | Modules`
            );
        }
        pendingTasks--;

        if (zipStream.isCancelled) {
          workers.forEach((w) => w.terminate());
          return resolve();
        }
        // If there are still files in the queue, assign the next one.
        if (fileQueue.length > 0) {
          const nextFile = fileQueue.shift();
          activeWorkers++;
          worker.postMessage({ file: { ...nextFile } });
        } else if (pendingTasks === 0) {
          // All files in the chunk are processed.
          workers.forEach((w) => w.terminate());
          resolve();
        }
      };
      workers.push(worker);
    }

    // Initially assign one file per worker.
    workers.forEach((worker) => {
      if (zipStream.isCancelled) {
        workers.forEach((w) => w.terminate());
        return resolve();
      }
      if (fileQueue.length > 0) {
        const fileObj = fileQueue.shift();
        activeWorkers++;
        worker.postMessage({ file: { ...fileObj } });
      }
    });
  });
};
